/**
 * Test App Push — Roomgrid layout (TestPageShell + RgSection).
 *
 * Sections: Account & application (incl. target groups + priority + type)
 * → Custom data → Actions → Content → Scheduling. Footer "Send test" CTA.
 */

import React, { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  getAccountsGroups,
  sendPushTestNotification,
} from "../services/testService";
import { buildAppPushPayload } from "../transforms/buildAppPushPayload";
import { DEFAULT_TIMEZONE } from "../constants";
import type { AppPushFormValues } from "../types/appPushTest";
import AccountAppPicker from "./sections/AccountAppPicker";
import SchedulingSection from "./sections/SchedulingSection";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

const PRIORITY_CHOICES: Array<{ id: "HIGH" | "LOW"; label: string }> = [
  { id: "HIGH", label: "HIGH" },
  { id: "LOW", label: "LOW" },
];

const createEmptyAppPushFormValues = (): AppPushFormValues => ({
  accountId: "",
  appId: "",
  groupIds: [],
  notification: {
    type: "",
    priority: "",
    data: [{ key: "", value: "" }],
    actions: [{ action: "", title: "" }],
  },
  content: {
    type: "CUSTOM",
    title: "",
    message: "",
    templateId: "",
    templateType: "",
    templateReturnType: "",
    language: "",
    variables: [{ key: "", value: "" }],
  },
  schedule: {
    type: "NONE",
    date: null,
    time: "",
    recurring: {
      intervalValue: 0,
      intervalUnit: "",
      expiresDate: null,
      expiresTime: "",
    },
    timezone: DEFAULT_TIMEZONE,
  },
});

export const AppPushTestPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm<AppPushFormValues>({
    defaultValues: createEmptyAppPushFormValues(),
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const accountId = useWatch({ control: methods.control, name: "accountId" });
  const appId = useWatch({ control: methods.control, name: "appId" });
  const contentType = useWatch({
    control: methods.control,
    name: "content.type",
  });

  const [groups, setGroups] = useState<string[]>([]);

  const dataFields = useFieldArray({
    control: methods.control,
    name: "notification.data",
  });
  const actionFields = useFieldArray({
    control: methods.control,
    name: "notification.actions",
  });

  const onSubmit = async (values: AppPushFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const body = buildAppPushPayload(values);
      const response = await sendPushTestNotification("appNotification", {
        ...body,
      });
      if (response.success) {
        notify("resources.test.notifications.appPushSent", {
          type: "success",
        });
        methods.reset(createEmptyAppPushFormValues());
      } else {
        notify(
          response.message ??
            translate("resources.test.notifications.appPushSendFailed"),
          { type: "error" },
        );
      }
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.appPushSendFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!accountId || !appId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    getAccountsGroups({ accountId, application: appId })
      .then((res) => {
        if (!cancelled) setGroups(res);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, appId]);

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey="resources.test.appPush.title"
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="groupIds"
                control={methods.control}
                rules={{
                  validate: (v) =>
                    (v && v.length > 0) ||
                    translate("resources.test.validation.groupRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    fullWidth
                    required
                    label={translate("resources.test.fields.groupId")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    SelectProps={{
                      multiple: true,
                      value: field.value ?? [],
                      onChange: field.onChange,
                      renderValue: (selected) =>
                        (selected as string[]).join(", "),
                    }}
                  >
                    {groups.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="notification.priority"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.messagePriorityRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label={translate("resources.test.fields.priority")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  >
                    {PRIORITY_CHOICES.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="notification.type"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.messageTypeRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={1}
                    label={translate("resources.test.fields.type")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.customData", {
            _: "Custom data",
          })}
        >
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            {dataFields.fields.map((f, i) => (
              <Box
                key={f.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Controller
                  name={`notification.data.${i}.key` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.dataKey")}
                    />
                  )}
                />
                <Controller
                  name={`notification.data.${i}.value` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.dataValue")}
                    />
                  )}
                />
                <IconButton
                  onClick={() => dataFields.remove(i)}
                  color="error"
                  sx={{ flexShrink: 0 }}
                  aria-label="remove data row"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="contained"
              size="small"
              onClick={() => dataFields.append({ key: "", value: "" })}
              sx={{ alignSelf: "flex-start" }}
            >
              {translate("resources.test.actions.addData")}
            </Button>
          </Stack>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.actions", {
            _: "Actions",
          })}
        >
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            {actionFields.fields.map((f, i) => (
              <Box
                key={f.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Controller
                  name={`notification.actions.${i}.action` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.action")}
                    />
                  )}
                />
                <Controller
                  name={`notification.actions.${i}.title` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.title")}
                    />
                  )}
                />
                <IconButton
                  onClick={() => actionFields.remove(i)}
                  color="error"
                  sx={{ flexShrink: 0 }}
                  aria-label="remove action row"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="contained"
              size="small"
              onClick={() => actionFields.append({ action: "", title: "" })}
              sx={{ alignSelf: "flex-start" }}
            >
              {translate("resources.test.actions.addActions")}
            </Button>
          </Stack>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.message", {
            _: "Message",
          })}
        >
          <Stack spacing={2}>
            <Controller
              name="content.type"
              control={methods.control}
              rules={{
                required: translate(
                  "resources.test.validation.messageTypeRequired",
                ),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  required
                  label={translate("resources.test.fields.messageType")}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                >
                  <MenuItem value="CUSTOM">
                    {translate("resources.test.choices.messageType.CUSTOM")}
                  </MenuItem>
                </TextField>
              )}
            />

            {contentType === "CUSTOM" && (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="content.title"
                    control={methods.control}
                    rules={{
                      required: translate(
                        "resources.test.validation.contentRequired",
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        multiline
                        minRows={1}
                        label={translate("resources.test.fields.title")}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="content.message"
                    control={methods.control}
                    rules={{
                      required: translate(
                        "resources.test.validation.contentRequired",
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        multiline
                        minRows={1}
                        label={translate("resources.test.fields.message")}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </Stack>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.scheduling", {
            _: "Scheduling",
          })}
          last
        >
          <SchedulingSection />
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default AppPushTestPage;
